/**
 * AdminAlertsConfig — Smart Alerts dashboard & configuration.
 * Sprint 11: Proactive alerts engine (B4).
 *
 * Shows all computed alerts grouped by severity, with configurable
 * thresholds and a "generate notifications" action to push alerts
 * to the client-facing notifications table.
 */
import { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { createNotification } from "../services/notifications.ts";
import { logAudit } from "../supabaseClient.js";
import { RADIUS, TRANSITION } from "../shared/theme.js";
import { Card, STitle, Badge } from "./adminUtils.jsx";

const SEVERITY_META = {
  critical: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Crítico" },
  warning: { color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", label: "Atenção" },
  info: { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", label: "Info" },
};

const ALERT_TYPE_LABELS = {
  review_expiring_7d: "Revisão Urgente",
  review_expiring_30d: "Revisão Próxima",
  suitability_expiring: "Suitability Expirando",
  aum_drop: "Queda de AUM",
  no_recent_snapshot: "Snapshot Desatualizado",
  crm_inactivity: "Inatividade CRM",
};

export default function AdminAlertsConfig({ alerts, summary, config, onNavigate, T }) {
  const [filter, setFilter] = useState("all");
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  const filteredAlerts = useMemo(() => {
    let list = alerts || [];
    // Filter dismissed
    list = list.filter((a) => !dismissedIds.has(a.id));
    // Filter by severity
    if (filter !== "all") {
      list = list.filter((a) => a.severity === filter);
    }
    return list;
  }, [alerts, filter, dismissedIds]);

  // Group alerts by type
  const groupedByType = useMemo(() => {
    const groups = {};
    for (const a of filteredAlerts) {
      if (!groups[a.type]) groups[a.type] = [];
      groups[a.type].push(a);
    }
    return groups;
  }, [filteredAlerts]);

  const handleDismiss = (id) => {
    setDismissedIds((prev) => new Set([...prev, id]));
  };

  const handleGenerateNotifications = useCallback(async () => {
    const criticalAlerts = (alerts || []).filter(
      (a) => a.severity === "critical" && a.clientId && !dismissedIds.has(a.id)
    );
    if (criticalAlerts.length === 0) return;

    setGenerating(true);
    let sent = 0;
    let failed = 0;

    for (const alert of criticalAlerts) {
      try {
        await createNotification(alert.clientId, alert.type, alert.label, alert.detail);
        sent++;
      } catch {
        failed++;
      }
    }

    setGenResult({ sent, failed, total: criticalAlerts.length });
    setGenerating(false);

    await logAudit("smart_alerts_generated", {
      total: criticalAlerts.length,
      sent,
      failed,
    }).catch(() => {});
  }, [alerts, dismissedIds]);

  const activeSeverity = summary || { total: 0, critical: 0, warning: 0, info: 0 };

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: 0, color: T.text, fontSize: 18, fontWeight: 700 }}>
          🔔 Alertas Inteligentes
        </h3>
        <p style={{ margin: "4px 0 0", color: T.muted, fontSize: 13 }}>
          Monitoramento proativo — CVM 30, revisões, AUM e atividade
        </p>
      </div>

      {/* Summary KPIs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          {
            label: "Total",
            value: activeSeverity.total - dismissedIds.size,
            color: T.text,
            filterKey: "all",
          },
          {
            label: "Críticos",
            value: activeSeverity.critical,
            color: SEVERITY_META.critical.color,
            filterKey: "critical",
          },
          {
            label: "Atenção",
            value: activeSeverity.warning,
            color: SEVERITY_META.warning.color,
            filterKey: "warning",
          },
          {
            label: "Informativo",
            value: activeSeverity.info,
            color: SEVERITY_META.info.color,
            filterKey: "info",
          },
        ].map((s) => (
          <button
            key={s.label}
            onClick={() => setFilter(s.filterKey)}
            style={{
              flex: 1,
              minWidth: 110,
              padding: "12px 16px",
              background: filter === s.filterKey ? s.color + "10" : T.card,
              borderRadius: RADIUS.md,
              border: `1px solid ${filter === s.filterKey ? s.color : T.border}`,
              cursor: "pointer",
              transition: TRANSITION.fast,
              textAlign: "left",
            }}
          >
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </button>
        ))}
      </div>

      {/* Actions bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ fontSize: 13, color: T.muted }}>
          {filteredAlerts.length} alerta(s) ativo(s)
          {dismissedIds.size > 0 && ` · ${dismissedIds.size} descartado(s)`}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {dismissedIds.size > 0 && (
            <button
              onClick={() => setDismissedIds(new Set())}
              style={{
                padding: "8px 16px",
                borderRadius: RADIUS.sm,
                border: `1px solid ${T.border}`,
                background: T.card,
                color: T.text,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Restaurar descartados
            </button>
          )}
          <button
            onClick={handleGenerateNotifications}
            disabled={generating || activeSeverity.critical === 0}
            style={{
              padding: "8px 20px",
              borderRadius: RADIUS.sm,
              border: "none",
              background: generating ? "#94A3B8" : "#DC2626",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: generating ? "wait" : "pointer",
              opacity: activeSeverity.critical === 0 ? 0.5 : 1,
            }}
          >
            {generating
              ? "⏳ Gerando..."
              : `🔔 Notificar ${activeSeverity.critical} alerta(s) crítico(s)`}
          </button>
        </div>
      </div>

      {/* Generation result */}
      {genResult && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: RADIUS.sm,
            background: genResult.failed > 0 ? "#FEF2F2" : "#F0FDF4",
            border: `1px solid ${genResult.failed > 0 ? "#FECACA" : "#BBF7D0"}`,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          ✅ {genResult.sent} notificação(ões) gerada(s)
          {genResult.failed > 0 && ` · ❌ ${genResult.failed} erro(s)`}
        </div>
      )}

      {/* Alerts grouped by type */}
      {filteredAlerts.length === 0 ? (
        <Card T={T} style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            Nenhum alerta ativo
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            Todos os clientes estão em dia com revisões, suitability e snapshots.
          </div>
        </Card>
      ) : (
        Object.entries(groupedByType).map(([type, typeAlerts]) => (
          <Card key={type} T={T} style={{ marginBottom: 14, padding: "16px 20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>{typeAlerts[0]?.icon || "⚠️"}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                    {ALERT_TYPE_LABELS[type] || type}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted }}>
                    {typeAlerts.length} cliente(s) afetado(s)
                  </div>
                </div>
              </div>
              <Badge
                text={SEVERITY_META[typeAlerts[0]?.severity]?.label || "Info"}
                color={SEVERITY_META[typeAlerts[0]?.severity]?.color || T.muted}
                bg={SEVERITY_META[typeAlerts[0]?.severity]?.bg || T.bg}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {typeAlerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    background: T.bg,
                    borderRadius: 6,
                    borderLeft: `3px solid ${SEVERITY_META[alert.severity]?.color || T.muted}`,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                      {alert.label}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>{alert.detail}</div>
                  </div>
                  <button
                    onClick={() => onNavigate && onNavigate(alert.nav)}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 4,
                      border: `1px solid ${T.border}`,
                      background: T.card,
                      color: T.text,
                      fontSize: 11,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {alert.nav} →
                  </button>
                  <button
                    onClick={() => handleDismiss(alert.id)}
                    title="Descartar"
                    style={{
                      padding: "4px 8px",
                      borderRadius: 4,
                      border: "none",
                      background: "transparent",
                      color: T.muted,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </Card>
        ))
      )}

      {/* Configuration info */}
      <Card T={T} style={{ marginTop: 20, padding: "16px 20px" }}>
        <STitle
          title="Configuração de Thresholds"
          sub="Parâmetros atuais do motor de alertas"
          T={T}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 10,
            marginTop: 8,
          }}
        >
          {[
            {
              label: "Revisão crítica",
              value: `≤ ${config?.reviewCriticalDays ?? 7} dias`,
              desc: "Antes do vencimento",
            },
            {
              label: "Revisão atenção",
              value: `≤ ${config?.reviewWarningDays ?? 30} dias`,
              desc: "Antes do vencimento",
            },
            {
              label: "Suitability",
              value: `≤ ${config?.suitabilityWarningDays ?? 30} dias`,
              desc: "Antes da expiração (12 meses)",
            },
            {
              label: "Queda AUM",
              value: `≥ ${config?.aumDropThresholdPct ?? 10}%`,
              desc: "Drop mês-a-mês",
            },
            {
              label: "Snapshot stale",
              value: `≥ ${config?.snapshotStaleDays ?? 45} dias`,
              desc: "Sem importação",
            },
            {
              label: "CRM inativo",
              value: `≥ ${config?.crmInactivityDays ?? 90} dias`,
              desc: "Sem contato registrado",
            },
          ].map((c) => (
            <div
              key={c.label}
              style={{
                padding: "10px 14px",
                background: T.bg,
                borderRadius: 6,
                border: `1px solid ${T.border}`,
              }}
            >
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{c.label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: "2px 0" }}>
                {c.value}
              </div>
              <div style={{ fontSize: 10, color: T.muted }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

AdminAlertsConfig.propTypes = {
  alerts: PropTypes.array.isRequired,
  summary: PropTypes.object.isRequired,
  config: PropTypes.object,
  onNavigate: PropTypes.func,
  T: PropTypes.object.isRequired,
};
